import { FabricText, Group, Rect } from 'fabric';
import type { FabricObject } from 'fabric';
import { applyFill } from '../fill';
import { registerRecipe } from '../registry';
import { tagSmartObject } from '../types';
import type {
  ContentSourceSpec,
  FillValue,
  ParamField,
  StandardContentPayload,
  ToolRecipe,
} from '../types';

/**
 * Data-table recipe: a poster-grade table of labeled rows and columns built
 * entirely from stock primitives (one Rect per cell, one FabricText per
 * non-empty cell). Cells given as "" stay empty on purpose — teachers print
 * tables students fill in by hand — so an empty string must render a blank
 * cell, never be "fixed" into placeholder text.
 *
 * Columns/rows arrive as list params — columns a string[], rows a string[][]
 * (json-era string params are still parsed); ragged rows are padded/truncated
 * to the column count and anything malformed falls back to sample data so
 * generate() never throws.
 *
 * CELL TAGGING (live on-canvas editing) — every text child generate() emits
 * carries a plain, inert `data` prop (survives toObject round-trips, never
 * affects rendering or export):
 *
 *   data: { tableCell: { kind, row?, col? } }
 *
 *   kind        indices        write-back target
 *   ----------  -------------  -------------------------------------------
 *   "title"     (none)         params.title
 *   "header"    col            params.columns[col]
 *   "rowLabel"  row            params.rows[row][0]  (rowLabels=true merely
 *                              restyles body column 0 — the label text lives
 *                              in the row array itself, so a rowLabel at row
 *                              r is the same string as a would-be cell at
 *                              {row: r, col: 0})
 *   "cell"      row, col       params.rows[row][col]
 *
 * `row`/`col` are 0-based indices into the PARSED arrays (parseColumns /
 * parseRows). For well-formed params these coincide exactly with how
 * params.columns / params.rows are addressed: parseRows pads/truncates each
 * row to the column count without reordering or dropping valid rows, so
 * tag {row: r, col: c} always maps to params.rows[r][c] and a header tag
 * {col: c} to params.columns[c]. Only when params are malformed (junk JSON,
 * no valid rows) do the indices address the sample fallback instead — an
 * editor writing back through a tag should therefore write the normalized
 * value and regenerate. Note that empty ("") cells produce no text object
 * and hence carry no tag; editors add text to a blank cell by writing
 * params.rows[row][col] and regenerating, not by picking it on canvas.
 */

const TEXT_COLOR = '#333333';

const SAMPLE_COLUMNS = ['Game', 'Tally', 'Total'];
const SAMPLE_ROWS = [
  ['Soccer', '', ''],
  ['Basketball', '', ''],
  ['Jump Rope', '', ''],
];

/** Keep pathological JSON from exploding into thousands of objects. */
const MAX_COLS = 16;
const MAX_ROWS = 40;

/** The inert tag stamped on every text child — see module header. */
export interface TableCellTag {
  kind: 'title' | 'header' | 'rowLabel' | 'cell';
  row?: number;
  col?: number;
}

/** Panel sections (ParamField.section) — shared spelling, typos break grouping. */
const SECTION_CONTENT = 'Content';
const SECTION_TYPOGRAPHY = 'Typography';
const SECTION_COLORS = 'Colors';
const SECTION_LAYOUT = 'Layout';

/**
 * A stored row is a plain array of cell strings (["Soccer", "", ""]) — the
 * exact shape generate() consumes and existing templates already contain, so
 * it must not change. A JS array is addressable through its numeric-string
 * keys, so the row-detail modal edits cells via keys "0".."15" ("Cell 1"…
 * "Cell 16", i.e. one field per possible column, cells beyond the 4th tucked
 * under Advanced). parseRows() pads/truncates every row to the live column
 * count, so values in cells past the real column count are simply ignored.
 */
const ROW_ITEM_SCHEMA: ParamField[] = Array.from({ length: MAX_COLS }, (_, i) => ({
  key: String(i),
  label: `Cell ${i + 1}`,
  type: 'text' as const,
  ...(i >= 4 ? { advanced: true } : {}),
}));

function asNumber(v: unknown, fallback: number): number {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : fallback;
}

function asString(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

function asBoolean(v: unknown, fallback: boolean): boolean {
  return typeof v === 'boolean' ? v : fallback;
}

/** Keep serialized coordinates short. */
function r2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Strings pass through, numbers become text, anything else is an empty cell. */
function asCell(v: unknown): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return '';
}

/** JSON param → parsed value (the editor hands us a string; regeneration an array). */
function parseJson(raw: unknown): unknown {
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

/** Column headers: array of strings (numbers tolerated). Junk → sample headers. */
function parseColumns(raw: unknown): string[] {
  const candidate = parseJson(raw);
  const cols: string[] = [];
  if (Array.isArray(candidate)) {
    for (const entry of candidate.slice(0, MAX_COLS)) {
      if (typeof entry === 'string' || typeof entry === 'number') cols.push(asCell(entry));
    }
  }
  return cols.length > 0 ? cols : [...SAMPLE_COLUMNS];
}

/**
 * Body rows: array of arrays of cell strings, each padded/truncated to the
 * column count ("" = intentionally blank cell). Non-array rows are dropped;
 * an empty result falls back to the sample rows reshaped to the columns.
 */
function parseRows(raw: unknown, colCount: number): string[][] {
  const candidate = parseJson(raw);
  const rows: string[][] = [];
  if (Array.isArray(candidate)) {
    for (const entry of candidate.slice(0, MAX_ROWS)) {
      if (!Array.isArray(entry)) continue;
      const row: string[] = [];
      for (let c = 0; c < colCount; c++) row.push(asCell(entry[c]));
      rows.push(row);
    }
  }
  if (rows.length > 0) return rows;
  return SAMPLE_ROWS.map((sample) => {
    const row: string[] = [];
    for (let c = 0; c < colCount; c++) row.push(asCell(sample[c]));
    return row;
  });
}

/**
 * Distribute `total` across columns evenly, except every column must be at
 * least `mins[i]` wide (enough for its header). Columns pinned at their
 * minimum drop out and the remainder is re-shared until stable; if the
 * minimums alone exceed `total` the table simply comes out wider — a header
 * must never be clipped on a poster.
 */
function distributeColumns(total: number, mins: number[]): number[] {
  const fixed = mins.map(() => false);
  for (;;) {
    const fixedSum = mins.reduce((s, m, i) => (fixed[i] ? s + m : s), 0);
    const flexCount = fixed.filter((f) => !f).length;
    if (flexCount === 0) return mins.slice();
    const share = (total - fixedSum) / flexCount;
    const violators = mins.map((m, i) => !fixed[i] && m > share);
    if (!violators.some(Boolean)) return mins.map((m, i) => (fixed[i] ? m : share));
    violators.forEach((v, i) => {
      if (v) fixed[i] = true;
    });
  }
}

/** Shrink a rendered text uniformly when it overflows the space it must fit. */
function fitText(text: FabricText, maxWidth: number): FabricText {
  const w = text.width ?? 0;
  if (w > maxWidth && w > 0) {
    const s = maxWidth / w;
    text.set({ scaleX: s, scaleY: s });
  }
  return text;
}

/* ----------------------------------------------------------------- table */

// ---------------------------------------------------------------------------
// Smart-fill seam — turn the table into a Term/Definition reference sheet
// from a standard's vocabulary.
// ---------------------------------------------------------------------------

const contentSource: ContentSourceSpec = {
  kind: 'standard',
  wants: ['vocabulary'],
  applyContent(payload: StandardContentPayload, current: Record<string, unknown>) {
    // Merge into a COPY of current so styling (fonts, fills, borders, layout
    // knobs) and rowLabels are never touched — only content (title/columns/
    // rows) changes.
    const next = { ...current };
    const vocab = Array.isArray(payload?.vocabulary) ? payload.vocabulary : [];
    if (vocab.length) {
      // `rows` is string[][] — parseRows() does `if (!Array.isArray(entry))
      // continue`, so an entry shaped {0:term,1:def} (looks array-like from
      // the itemSchema's "0"/"1" keys, but ISN'T a real array) is silently
      // DROPPED and the table falls back to SAMPLE_ROWS — a mismatch here is
      // exactly the "silent no-render" trap. Every row must be a real
      // 2-element array, capped at this file's own MAX_ROWS.
      next.columns = ['Term', 'Definition'];
      next.rows = vocab.slice(0, MAX_ROWS).map((v) => [v?.term ?? '', v?.definition ?? '']);
    }
    // Gate the title on content actually landing (word-wall/misconception-
    // card precedent, owner 2026-07-24): a title-only patch on an empty
    // payload would misfire the modal's "changed" detection.
    const code = payload?.standard?.code;
    if (code && vocab.length) next.title = `Vocabulary Reference — ${code}`;
    // Provenance (survives regenerate — declared schema fields).
    if (payload?.standard?.code) next.sourceStandardCode = payload.standard.code;
    if (payload?.standard?.id) next.sourceStandardId = payload.standard.id;
    return next;
  },
};

const dataTable: ToolRecipe = {
  id: 'data-table',
  domain: 'math',
  tags: ['table', 'data', 'grid', 'chart', 'rows', 'columns', 'organizer'],
  name: 'Data Table',
  category: 'chart',
  version: 1,
  description:
    'Table with labeled columns and rows; empty ("") cells print blank for students to fill in.',
  defaults: {
    title: '',
    columns: SAMPLE_COLUMNS,
    rows: SAMPLE_ROWS,
    rowLabels: true,
    width: 480,
    rowHeight: 48,
    fontFamily: 'Arial',
    fontSize: 16,
    // 0 = "auto": header size derives from fontSize (× 1.05) exactly as it
    // always has, so params saved before this knob existed render identically.
    headerFontSize: 0,
    textAlign: 'center',
    headerFill: '#54A0FF',
    headerTextColor: '#FFFFFF',
    cellFill: '#FFFFFF',
    altRowFill: '#EAF2FF',
    borderColor: '#2C3E50',
    borderWidth: 2,
    textColor: TEXT_COLOR,
    cornerRadius: 12,
    // 0 = "auto": padding derives from fontSize (× 0.9), the historic rule.
    cellPaddingX: 0,
    showGrid: true,
    equalColumns: false,
    sourceStandardCode: '',
    sourceStandardId: '',
  },
  schema: [
    {
      key: 'title',
      label: 'Title',
      type: 'text',
      section: SECTION_CONTENT,
      help: 'Bold bar above the table. Leave empty for none.',
    },
    {
      key: 'columns',
      label: 'Columns',
      type: 'list',
      itemType: 'text',
      minItems: 1,
      maxItems: MAX_COLS,
      section: SECTION_CONTENT,
      help: 'One header per column.',
    },
    {
      key: 'rows',
      label: 'Rows',
      type: 'list',
      itemSchema: ROW_ITEM_SCHEMA,
      summaryKeys: ['0', '1'],
      minItems: 1,
      maxItems: MAX_ROWS,
      section: SECTION_CONTENT,
      help: 'One entry per row — open it to fill the cells left to right. Leave a cell empty to print a blank box students fill in.',
    },
    {
      key: 'rowLabels',
      label: 'Row labels',
      type: 'boolean',
      section: SECTION_CONTENT,
      help: 'Style the first column like the header (colored fill, bold text).',
    },
    { key: 'fontFamily', label: 'Font', type: 'font', section: SECTION_TYPOGRAPHY },
    {
      key: 'fontSize',
      label: 'Text size',
      type: 'number',
      min: 8,
      max: 120,
      step: 1,
      section: SECTION_TYPOGRAPHY,
    },
    {
      key: 'headerFontSize',
      label: 'Header size',
      type: 'number',
      min: 0,
      max: 120,
      step: 1,
      section: SECTION_TYPOGRAPHY,
      help: '0 = automatic (slightly larger than the text size).',
    },
    {
      key: 'textAlign',
      label: 'Cell alignment',
      type: 'select',
      options: [
        { value: 'left', label: 'Left' },
        { value: 'center', label: 'Center' },
        { value: 'right', label: 'Right' },
      ],
      section: SECTION_TYPOGRAPHY,
      help: 'Body cells only — the title, headers, and row labels stay centered.',
    },
    {
      key: 'headerFill',
      label: 'Header fill',
      type: 'fill',
      section: SECTION_COLORS,
      help: 'Solid color, gradient, or texture behind the title bar, header row, and row labels.',
    },
    {
      key: 'headerTextColor',
      label: 'Header text',
      type: 'fill',
      section: SECTION_COLORS,
      help: 'Solid, gradient, or texture for the title, headers, and row labels.',
    },
    {
      key: 'cellFill',
      label: 'Cell fill',
      type: 'fill',
      section: SECTION_COLORS,
      help: 'Solid, gradient, or texture behind the body cells.',
    },
    {
      key: 'altRowFill',
      label: 'Banding fill',
      type: 'fill',
      section: SECTION_COLORS,
      help: 'Every other row. Empty disables banding. Solid, gradient, or texture.',
    },
    {
      key: 'textColor',
      label: 'Text color',
      type: 'fill',
      section: SECTION_COLORS,
      help: 'Solid, gradient, or texture for the body-cell text.',
    },
    { key: 'borderColor', label: 'Border color', type: 'color', section: SECTION_COLORS },
    // Layout. width/rowHeight stay advanced — resize-sync owns size now, so
    // hand-typing them is the rare path; cornerRadius/borderWidth are the
    // genuinely rare knobs. The rest are everyday layout switches.
    {
      key: 'width',
      label: 'Width',
      type: 'number',
      min: 120,
      max: 4000,
      step: 10,
      advanced: true,
      section: SECTION_LAYOUT,
    },
    {
      key: 'rowHeight',
      label: 'Row height',
      type: 'number',
      min: 24,
      max: 400,
      step: 2,
      advanced: true,
      section: SECTION_LAYOUT,
    },
    {
      key: 'cellPaddingX',
      label: 'Cell padding',
      type: 'number',
      min: 0,
      max: 200,
      step: 1,
      section: SECTION_LAYOUT,
      help: 'Horizontal space inside each cell. 0 = automatic (scales with text size).',
    },
    {
      key: 'showGrid',
      label: 'Grid lines',
      type: 'boolean',
      section: SECTION_LAYOUT,
      help: 'Inner cell borders. The outer frame always shows.',
    },
    {
      key: 'equalColumns',
      label: 'Equal columns',
      type: 'boolean',
      section: SECTION_LAYOUT,
      help: 'Force every column to the same width instead of sharing by header size.',
    },
    {
      key: 'borderWidth',
      label: 'Border width',
      type: 'number',
      min: 1,
      max: 20,
      step: 1,
      advanced: true,
      section: SECTION_LAYOUT,
    },
    {
      key: 'cornerRadius',
      label: 'Corner radius',
      type: 'number',
      min: 0,
      max: 60,
      step: 1,
      advanced: true,
      section: SECTION_LAYOUT,
    },
    {
      key: 'sourceStandardCode',
      label: 'Source standard code',
      type: 'text',
      section: SECTION_CONTENT,
      advanced: true,
      help: 'Set automatically when filled from a standard.',
    },
    {
      key: 'sourceStandardId',
      label: 'Source standard id',
      type: 'text',
      section: SECTION_CONTENT,
      advanced: true,
      help: 'Set automatically when filled from a standard.',
    },
  ],
  // Smart Tools modal exemplar (SM-A, 2026-07-23): title/columns/rows ARE the
  // table — hero them (rowLabels stays the lone Content-card field); the many
  // Typography/Colors/Layout knobs read best two-up in the wide modal.
  modal: {
    hero: ['title', 'columns', 'rows'],
    columns: 2,
    sectionOrder: [SECTION_CONTENT, SECTION_TYPOGRAPHY, SECTION_COLORS, SECTION_LAYOUT],
  },
  // Honest band values (schema-checked ranges): row height + text scale down
  // as fine-motor/reading maturity rises; frames soften (fat rounded borders
  // K-2 → hairline near-square 9-12). Faces: Fredoka → Nunito → Lexend →
  // Arial, same ladder as the charts.
  gradeBands: {
    K2: { fontFamily: 'Fredoka', fontSize: 24, rowHeight: 72, borderWidth: 3, cornerRadius: 16 },
    G35: { fontFamily: 'Nunito', fontSize: 18, rowHeight: 56, borderWidth: 2, cornerRadius: 12 },
    G68: { fontFamily: 'Lexend', fontSize: 16, rowHeight: 48, borderWidth: 2, cornerRadius: 8 },
    G912: { fontFamily: 'Arial', fontSize: 14, rowHeight: 40, borderWidth: 1, cornerRadius: 4 },
  },
  contentSource,
  async generate(params) {
    const title = asString(params.title, '').trim();
    const columns = parseColumns(params.columns);
    const rows = parseRows(params.rows, columns.length);
    const rowLabels = asBoolean(params.rowLabels, true);
    const width = Math.max(120, asNumber(params.width, 480));
    const fontSize = Math.max(8, asNumber(params.fontSize, 16));
    const rowHeight = Math.max(fontSize * 1.5, asNumber(params.rowHeight, 48));
    const fontFamily = asString(params.fontFamily, 'Arial');
    // FillValue: legacy solid strings pass straight through resolveFill, so
    // old templates render byte-identically; richer fills resolve to
    // Gradient/Pattern with a solid fallback on failure. header text, cell
    // fill, banding, and body text all wear fabric `fill`, so all take a
    // FillValue (fabric text fill honors gradients/patterns too). borderColor
    // stays a plain color — it lands on strokes only (house Border law).
    const headerFill = (params.headerFill ?? '#54A0FF') as FillValue;
    const headerTextFill = (params.headerTextColor ?? '#FFFFFF') as FillValue;
    const cellFill = (params.cellFill ?? '#FFFFFF') as FillValue;
    const altRowFill = (params.altRowFill ?? '#EAF2FF') as FillValue;
    const borderColor = asString(params.borderColor, '#2C3E50');
    const borderWidth = Math.max(1, asNumber(params.borderWidth, 2));
    const textFill = (params.textColor ?? TEXT_COLOR) as FillValue;
    const cornerRadius = Math.max(0, asNumber(params.cornerRadius, 12));
    // New knobs (2026-07): every default reproduces the historic output
    // exactly, so params saved before they existed render identically and the
    // recipe version stays 1.
    const rawAlign = asString(params.textAlign, 'center');
    const textAlign: 'left' | 'center' | 'right' =
      rawAlign === 'left' || rawAlign === 'right' ? rawAlign : 'center';
    const headerFontSizeParam = asNumber(params.headerFontSize, 0);
    const headerFontSize = headerFontSizeParam > 0 ? headerFontSizeParam : fontSize * 1.05;
    const cellPaddingX = asNumber(params.cellPaddingX, 0);
    // generous cell padding — poster, not spreadsheet
    const padX = cellPaddingX > 0 ? cellPaddingX : fontSize * 0.9;
    const showGrid = asBoolean(params.showGrid, true);
    const equalColumns = asBoolean(params.equalColumns, false);

    // Inner grid strokes (title bar, header row, body cells). The rounded
    // outer frame is drawn unconditionally at the end.
    const gridStroke = showGrid ? borderColor : '';
    const gridStrokeWidth = showGrid ? borderWidth : 0;

    // Header texts double as measurements: each column must be at least wide
    // enough for its (bold) header; the rest share the remaining width.
    // Header-text fill is applied at the end (a FillValue may be a gradient/
    // pattern, not a constructor color); .width is fill-independent so column
    // sizing below is unaffected.
    const headerTexts = columns.map(
      (label) =>
        new FabricText(label, {
          fontFamily,
          fontSize: headerFontSize,
          fontWeight: 'bold',
        }),
    );
    const mins = headerTexts.map((t) => (t.width ?? 0) + padX * 2);
    // equalColumns: every column identical, wide enough for the widest
    // header (a header must never be clipped, same rule as distribution).
    const colWidths = equalColumns
      ? columns.map(() => Math.max(width / columns.length, ...mins))
      : distributeColumns(width, mins);
    const gridW = colWidths.reduce((s, w) => s + w, 0);

    // Frame margin: the rounded backdrop extends past the rectangular grid by
    // enough that square grid corners stay inside the rounded outer corners
    // (a corner of radius r cuts in by ~0.3r at 45°).
    const margin = Math.max(borderWidth * 2, cornerRadius * 0.35, 4);
    const titleH = title ? rowHeight * 1.2 : 0;
    const headerH = rowHeight * 1.2; // header row visually dominant
    const gridH = titleH + headerH + rows.length * rowHeight;
    const totalW = gridW + margin * 2;
    const totalH = gridH + margin * 2;

    const objects: FabricObject[] = [];
    // Objects grouped by which FillValue they wear — each filled in one
    // applyFill pass at the end (matches the historic headerRects pattern).
    //   headerRects   → headerFill  (title bar, header row, row-label cells)
    //   headerTextObjs→ headerTextFill (title / header / row-label text)
    //   cellRects     → cellFill    (backdrop + plain body cells)
    //   bandRects     → altRowFill  (banded body cells)
    //   bodyTextObjs  → textFill    (plain body-cell text)
    const headerRects: Rect[] = [];
    const headerTextObjs: FabricObject[] = [...headerTexts];
    const cellRects: FabricObject[] = [];
    const bandRects: FabricObject[] = [];
    const bodyTextObjs: FabricObject[] = [];

    // Rounded backdrop behind everything — this is what gives the table its
    // rounded outer look while the cells themselves stay rectangular.
    const backdrop = new Rect({
      left: 0,
      top: 0,
      width: r2(totalW),
      height: r2(totalH),
      rx: cornerRadius,
      ry: cornerRadius,
    });
    cellRects.push(backdrop);
    objects.push(backdrop);

    const x0 = margin;
    const y0 = margin;
    // Left edge of each column, plus the trailing edge.
    const colX: number[] = [x0];
    for (const w of colWidths) colX.push(colX[colX.length - 1] + w);

    // Title bar: bold header-colored band across the grid, above the table.
    if (title) {
      const titleRect = new Rect({
        left: r2(x0),
        top: r2(y0),
        width: r2(gridW),
        height: r2(titleH),
        stroke: gridStroke,
        strokeWidth: gridStrokeWidth,
      });
      headerRects.push(titleRect);
      objects.push(titleRect);
      const titleText = fitText(
        new FabricText(title, {
          fontFamily,
          fontSize: fontSize * 1.25,
          fontWeight: 'bold',
        }),
        gridW - padX * 2,
      )
        .set({
          left: r2(x0 + gridW / 2),
          top: r2(y0 + titleH / 2),
          originX: 'center',
          originY: 'center',
        })
        .set('data', { tableCell: { kind: 'title' } satisfies TableCellTag });
      headerTextObjs.push(titleText);
      objects.push(titleText);
    }

    // Header row.
    const headerTop = y0 + titleH;
    columns.forEach((_, c) => {
      const headerRect = new Rect({
        left: r2(colX[c]),
        top: r2(headerTop),
        width: r2(colWidths[c]),
        height: r2(headerH),
        stroke: gridStroke,
        strokeWidth: gridStrokeWidth,
      });
      headerRects.push(headerRect);
      objects.push(headerRect);
      objects.push(
        fitText(headerTexts[c], colWidths[c] - padX * 2)
          .set({
            left: r2(colX[c] + colWidths[c] / 2),
            top: r2(headerTop + headerH / 2),
            originX: 'center',
            originY: 'center',
          })
          .set('data', { tableCell: { kind: 'header', col: c } satisfies TableCellTag }),
      );
    });

    // Body rows: a Rect per cell (so blanks still have visible boxes), text
    // only where the cell is non-empty.
    rows.forEach((row, ri) => {
      const rowTop = headerTop + headerH + ri * rowHeight;
      const banded = altRowFill !== '' && ri % 2 === 1;
      row.forEach((cell, c) => {
        const isLabel = rowLabels && c === 0;
        const cellRect = new Rect({
          left: r2(colX[c]),
          top: r2(rowTop),
          width: r2(colWidths[c]),
          height: r2(rowHeight),
          stroke: gridStroke,
          strokeWidth: gridStrokeWidth,
        });
        // Label cells wear the header fill (headerRects overwrites any cell
        // fill); plain cells take banding or the base cell fill.
        if (isLabel) headerRects.push(cellRect);
        else (banded ? bandRects : cellRects).push(cellRect);
        // The RECT carries the same tag as its text: empty cells have no text
        // object, and editors need a dblclick target to type into a blank
        // fill-in cell (the text object appears on the next regenerate).
        cellRect.set('data', {
          tableCell: (isLabel
            ? { kind: 'rowLabel', row: ri }
            : { kind: 'cell', row: ri, col: c }) satisfies TableCellTag,
        });
        objects.push(cellRect);
        if (cell.trim() !== '') {
          const text = fitText(
            new FabricText(cell, {
              fontFamily,
              fontSize,
              fontWeight: isLabel ? 'bold' : 'normal',
            }),
            colWidths[c] - padX * 2,
          );
          // Row-label text wears the header-text fill; plain cells the body
          // text fill — resolved in the applyFill pass at the end.
          (isLabel ? headerTextObjs : bodyTextObjs).push(text);
          // textAlign applies to plain body cells only; row labels stay
          // centered like the header they mirror.
          if (isLabel || textAlign === 'center') {
            text.set({
              left: r2(colX[c] + colWidths[c] / 2),
              top: r2(rowTop + rowHeight / 2),
              originX: 'center',
              originY: 'center',
            });
          } else if (textAlign === 'left') {
            text.set({
              left: r2(colX[c] + padX),
              top: r2(rowTop + rowHeight / 2),
              originX: 'left',
              originY: 'center',
            });
          } else {
            text.set({
              left: r2(colX[c] + colWidths[c] - padX),
              top: r2(rowTop + rowHeight / 2),
              originX: 'right',
              originY: 'center',
            });
          }
          const tag: TableCellTag = isLabel
            ? { kind: 'rowLabel', row: ri }
            : { kind: 'cell', row: ri, col: c };
          text.set('data', { tableCell: tag });
          objects.push(text);
        }
      });
    });

    // Resolve every fill once per object it lands on; a dead texture URL falls
    // back to the historic default so the table always renders. A plain solid
    // string resolves to itself, so pre-fill templates render byte-identically.
    await Promise.all([
      ...headerRects.map((r) => applyFill(r, headerFill, '#54A0FF')),
      ...headerTextObjs.map((t) => applyFill(t, headerTextFill, '#FFFFFF')),
      ...cellRects.map((r) => applyFill(r, cellFill, '#FFFFFF')),
      ...bandRects.map((r) => applyFill(r, altRowFill, '#EAF2FF')),
      ...bodyTextObjs.map((t) => applyFill(t, textFill, TEXT_COLOR)),
    ]);

    // Heavier rounded outer border on top of everything. evented:false —
    // fabric hit-tests unfilled rects by bounding box, so without it this
    // topmost frame swallows every child hit and dblclick-to-edit-a-cell
    // (subTargetCheck) can never reach the tagged cells beneath.
    objects.push(
      new Rect({
        left: 0,
        top: 0,
        width: r2(totalW),
        height: r2(totalH),
        fill: '',
        stroke: borderColor,
        strokeWidth: borderWidth * 2,
        rx: cornerRadius,
        ry: cornerRadius,
        evented: false,
      }),
    );

    const group = new Group(objects);
    group.set({ left: 0, top: 0 });
    return tagSmartObject(group, dataTable, params);
  },
};

registerRecipe(dataTable);
